<template>
  <div class="orderlist">
    <div v-if="loading" class="subheading">
      <br /><br />Loading order {{ oid }}...
      <br />
      <v-progress-circular indeterminate></v-progress-circular>
    </div>
    <div v-else-if="notfound" class="subheading">
      <br /><br />
      Order {{ oid }}  was not found. Check the search options on the Orders page to find it.
    </div>
    <v-card v-else>
        <v-layout mr-4 align-center justify-space-between row>
          <v-card-title class="headline">Order</v-card-title>
          <v-card-title :class="statusClass">Status:{{ orderStatus }}</v-card-title >
        </v-layout>
        <h2 class=" text-xs-left ">ID: {{ oid }}</h2>
        <h2 class=" text-xs-left ">Buyer: {{ order.buyerDetails.name }}
          <v-btn flat :loading="loadingEmailBuyer" @click="anonEmailBuyer">email buyer</v-btn>
          <span class="error--text" v-if="getEmailError">{{getEmailError}}</span>
        </h2>

        <div class="ml-4 text-xs-left subheading ">
          {{ orderDates }}

          <v-btn v-if="showReopenBtn" :loading="reopenProcessing" flat class="accent--text" @click="doReopen">Re-open Order</v-btn>
          <v-btn  v-if="showCancelBtn" :loading="cancelProcessing" @click.stop="showCancelDialog" flat class="accent--text">Cancel Order</v-btn>

          <!-- This is the dialog box that shows when someone tries to cancel an order -->
          <v-dialog lazy v-model="cancelDialog" persistent max-width="600px">
            <v-card>
              <v-card-text>
                <span class="subheading bold">Are you sure you want to cancel this order?</span>
                <v-checkbox label="Send notification to buyer" v-model="sendCancelEmail"></v-checkbox>
                <v-text-field label="Reason for cancel (optional)" v-model="sendCancelReason"></v-text-field>
              </v-card-text>
              <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn color="blue darken-1" flat @click="cancelDialog = false">Leave Order Open</v-btn>
                <v-btn color="blue darken-1" flat @click="doCancel">Cancel Order</v-btn>
              </v-card-actions>
            </v-card>
          </v-dialog>
          <span v-if="cancelError" class="error--text">Error: {{ cancelError }}</span>
          <span v-if="cancelSucceeded" class="success--text">Succeeded</span>
        </div>
        <hr />
          <!-- Payment info -->
          <v-layout mr-4 align-center justify-space-between row>
            <h2>Payment</h2>
            <div :class="paymentClass">
                 {{ paymentReceivedMessage }}
            </div>
          </v-layout>

          <div class="ml-4 text-xs-left subheading">
            Payment type: <b>{{ paymentType }}</b><br />
            <table class="mt-2 mb-2">
              <tr>
                  <td>Item subtotal:</td><td>{{ itemsPrice }}</td>
              </tr>
              <tr>
                  <td>Shipping:</td><td>{{ shippingPrice }}</td>
              </tr>
              <tr class="bottomLine">
                  <td>Tax:</td><td>{{ taxPrice }}</td>
              </tr>
              <tr>
                  <td>Expected payment:</td><td><b>{{ formattedPrice }}</b></td>
              </tr>

            </table>
            </v-layout>
            <div v-if="isCreditCard">
              This payment is processed using Stripe on your Stripe account.<br />
              Stripe charge ID: {{ stripeTransactionID }}<br />
              Status: {{ creditCardStatus }}
              <v-btn v-if="showRefundButton" flat class="accent--text" @click="sendRefund" :loading="refundProcessing">Refund Payment</v-btn>
              <span v-if="refundSucceeded" class="success--text">Refund Succeeded</span>
              <span v-if="refundError" class="error--text">Error: {{refundError}}</span>
            </div>
            <div v-else>
              <v-layout align-center row fill-height >
                Payment received:
                <v-checkbox height=1 v-model="order.paymentReceived" @click="togglePaymentReceived" style="flex:0"></v-checkbox>
                <v-progress-circular v-if="paymentLoading" indeterminate></v-progress-circular>
                <span v-if="paymentError" class="error--text">{{paymentError}}</span>
              </v-layout>
              {{paymentInstructions}}<br />
            </div>

            <v-tooltip right lazy color="info">
              <v-btn slot="activator" :loading="sendingInvoice" @click="resendInvoice">Resend Confirmation</v-btn>
              <span>This button will resend an email invoice to the buyer.</span>
            </v-tooltip>
            <span v-if="resendError" class="error--text">Error: {{resendError}}</span>
            <span v-if="resendSuccess" class="success--text">Invoice resent successfully</span>

          </div>

          <!-- End of Payment info -->

          <hr />
          <!-- Delivery info -->
          <v-layout mr-4 align-center justify-space-between row>
            <h2>Delivery</h2>
            <div :class="deliveryClass">
                 {{ deliveryMessage }}
            </div>
          </v-layout>

          <div class="ml-4 text-xs-left subheading">
            Delivery type: {{ deliveryType }}<br />
            <v-layout v-if="deliveryLocation"><!-- Show addr if present -->
              <span>Location:</span>
              <span>{{this.order.deliveryDetails.address.street}}<br />
                  {{this.order.deliveryDetails.address.city}}, {{this.order.deliveryDetails.address.state}} {{this.order.deliveryDetails.address.zip5}}
              </span>
            </v-layout>
            <v-layout align-center row fill-height >
              Item(s) delivered:
              <v-checkbox height=1 v-model="order.delivered" @click="toggleDelivery" style="flex:0"></v-checkbox>
              <v-progress-circular v-if="deliveryLoading" indeterminate></v-progress-circular>
              <span v-if="deliveryError" class="error--text">{{deliveryError}}</span>
            </v-layout>
            {{deliveryInstructions}}<br />
          </div>

          <!-- End of Delivery info -->

          <!-- Items -->
          <hr />

          <h2>Items</h2>
          <v-data-table
            :headers="itemHeaders"
            :items="order.cartData.items"
            hide-actions
            class="elevation-1"
          >
            <template
              slot="items"
              slot-scope="props"
            >
              <tr @click='viewItem(props.item)'>
                <td class="body-2 text-xs-left">
                  <img v-if="props.item.item.thumbnails[0]" :src=props.item.item.thumbnails[0].thumbDownloadURL  />
                  <div v-else>
                    No Image
                  </div>
                </td>
                <td class="body-2 text-xs-left">{{ props.item.item.name }}</td>
                <td class="body-2 text-xs-right">{{ props.item.numBought }}</td>
                <td class="body-2 text-xs-right">{{ formatCurrency(props.item.item.price) }}</td>
              </tr>
            </template>
          </v-data-table>
          <!-- End of Items -->

          <hr />
          <!-- Comment from Buyer -->
          <v-layout mr-4 align-center justify-space-between row>
            <h2>Note</h2>
          </v-layout>

          <div class="ml-4 text-xs-left subheading">
            {{ note }}<br />
          </div>

          <!-- End of Comment Section -->

    </v-card>
</div>

</template>

<script>
import {mapState} from 'vuex'
import utils from '../../utils.ts'
import config from '../../config.js'
import firebase from 'firebase/app'


export default {
  name: 'ViewOrder',
  components: {
  },
  data () {
    return {
      loading: true,
      notfound: false,
      loadingEmailBuyer: false,
      getEmailError: null,
      reopenProcessing: false,
      cancelDialog: false,
      sendCancelEmail: true,
      sendCancelReason: '',
      cancelSucceeded: false,
      cancelProcessing: false,
      cancelError: null,
      showCancelBtn: false,
      showReopenBtn: false,
      orderDates: '',
      paymentLoading: false,
      deliveryLoading: false,
      sendingInvoice: false,
      resendError: null,
      resendSuccess: null,
      refundProcessing: false,
      refundError: null,
      refundSucceeded: false,
      oid: this.$route.params.oid,
      orderStatus: null,
      stripeTransactionID: null,
      creditCardStatus: '',
      showRefundButton: false,
      itemHeaders: [
        {text: 'Item', value: 'thumbnail', align: 'left'},
        {text: 'Item Name', value: 'name', align: 'left'},
        {text: '# items', value: 'itemNumber', align: 'right'},
        {text: 'Item Cost', value: 'cost', align: 'right'}
      ],
      statusClass: null,
      deliveryClass: null,
      paymentClass: null,
      mailtoLink: null,
      note: '',
      deliveryType: 'Unknown',
      deliveryLocation: null,
      paymentType: null,
      isCreditCard: false,
      paymentReceivedMessage: null,
      deliveryMessage: null,
      deliveryInstructions: null,
      paymentInstructions: null,
      deliveryError: null,
      paymentError: null,
      order:null,
      orderWatcher: null,
    }
  },
  watch: {
    oid: function (val) {
      console.log('OID CHANGE' + val)
      this.loadOrder(val, this.orders)
    },
    orders: function (val) {
      // console.log('ORDERS CHANGE' + val)
      this.loadOrder(this.oid, val)
    }
  },
  computed: {
    ...mapState({
      uid: (state) => state.admin.uid,
      orders: state => state.admin.orders,
      aid: state => state.admin.userInfo.aid,
      sellerName: state => state.admin.userInfo.name,
      locked: state => state.admin.public_settings.locked,
    }),
    // order: function () {
    //   for (let i = 0; i < this.orders.length; i++) {
    //     if (this.orders[i].id === this.oid) {
    //       return this.orders[i]
    //     }
    //   }
    //   return null
    // },
    itemsPrice : function () {
      if (this.order.itemsPrice) {
          return utils.formatCurrency(this.order.itemsPrice)
      } else {
        return utils.formatCurrency(0)
      }
    },
    formattedPrice: function () {
      if (this.order.totalPrice) {
        return utils.formatCurrency(this.order.totalPrice)
      } else {
        return utils.formatCurrency(0)
      }
    },
    taxPrice: function () {
      if (this.order.tax) {
        return utils.formatCurrency(this.order.tax)
      } else {
        return utils.formatCurrency(0)
      }
    },
    shippingPrice: function () {
      if (this.order.shipping) {
        return utils.formatCurrency(this.order.shipping)
      } else {
        return utils.formatCurrency(0)
      }
    },
    sellerEmail: function () {
      return `${this.aid}@smartkidsmarket.com`
    }

  },

  beforeDestroy: function () {
    // Stop watching an order if we had to watch one
    if (this.orderWatcher) {
      this.orderWatcher()
      this.orderWatcher = null
    }
  },
  mounted: function () {
    this.loadOrder(this.oid, this.orders)
  },
  methods: {
    anonEmailBuyer: async function () {
      // Get an anonymous email for this buyer and then open an email
      // client for them.
      console.log('Getting anon email...')
      this.loadingEmailBuyer = true
      this.getEmailError = false

      var func = config.getFunctions().httpsCallable('getEmailToAnonMapping')

      // Call the async function to refund
      try {

        if (this.locked) {
          throw new Error("Cannot email from a demo (locked) account.")
        }

        console.log("Buyer Deets email: "+this.order.buyerDetails.email)
        const data = await func({
          'email': this.order.buyerDetails.email
        })

        // {"data":{"anonEmail":"dan9@smartkidsmarket-test.com","parentEmail":"pepperfleck@gmail.com"}}
        const anonEmail = data['data']['anonEmail']

        // Now open the email client
        console.log('Got anon email: ' + anonEmail)
        const emailSub = `SmartKidsMarket Order: ${this.oid} Information`
        let emailBody = `Hello ${this.order.buyerDetails.name},

Here is some information about your SmartKidsMarket.com order.

Thanks,
${this.sellerName} (${this.sellerEmail})
`
        emailBody = encodeURIComponent(emailBody)
        const mailToLink = `mailto:${anonEmail}?subject=${emailSub}&body=${emailBody}`
        window.open(mailToLink, '_self')
      } catch (err) {
        console.error('ERROR getting email to anon mapping: ' + err)
        this.getEmailError = err.message
      }

      this.loadingEmailBuyer = false
    },
    formatCurrency: function (v) {
      return utils.formatCurrency(v)
    },
    showCancelDialog: function () {
      this.cancelSucceeded = false
      this.cancelError = false

      if (this.locked) {
        this.cancelError = 'Account is locked. You cannot make any changes.'
        return
      }

      // console.log('DE: ' + this.order.stripeCharge.refunded)
      // If it's paid, they can't cancel!
      if (this.order.paymentReceived && !(this.order.stripeCharge && this.order.stripeCharge.refunded)) {
        this.cancelError = 'You cannot cancel a paid order. You must refund the money first and then uncheck "Payment Received".'
        return
      }
      this.cancelDialog = true
    },
    doCancel: async function () {
      this.cancelDialog = false // Close the dialog
      this.cancelSucceeded = false
      this.cancelError = false

      // Cancel the order
      this.cancelProcessing = true

      var cancelOrder = config.getFunctions().httpsCallable('cancelOrder')

      // Call the async function to refund
      try {
        await cancelOrder({
          'orderID': this.oid,
          'sendCancelEmail': this.sendCancelEmail, // Send buyer notification ?
          'reason': this.sendCancelReason // Log a reason
        })
        this.cancelSucceeded = true
      } catch (err) {
        console.error('ERROR canceling: ' + JSON.stringify(err))
        this.cancelError = err.message
      }
      this.cancelProcessing = false
    },
    doReopen: async function () {
      // Reopen a canceled order
      this.cancelSucceeded = false
      this.cancelError = false
      this.reopenProcessing = true

      var reopenOrder = config.getFunctions().httpsCallable('reopenOrder')

      // Call the async function to refund
      try {
        if (this.locked) {
          throw Error('Account is locked. You cannot make any changes.')
        }

        await reopenOrder({
          'orderID': this.oid
        })
        this.cancelSucceeded = true
      } catch (err) {
        console.error('ERROR reopening: ' + JSON.stringify(err))
        this.cancelError = err.message
      }
      this.reopenProcessing = false
    },
    _showOrder: function (order) {
      this.order = order
      this.deliveryClass = order.delivered ? 'complete success' : 'incomplete error'
      this.paymentClass = order.paymentReceived ? 'complete success' : 'incomplete error'
      this.mailtoLink = `mailto:${order.buyerDetails.email}?subject=SmartKidsMarket order ${order.id}`
      this.note = order.note ? order.note : 'No note'
      this.statusClass = order.orderStatus !== 'OPEN' ? 'complete success' : 'incomplete info'

      this.orderStatus = order.orderStatus

      this.showReopenBtn = order.orderStatus === 'CANCELED'
      this.showCancelBtn = order.orderStatus === 'OPEN'

      this.orderDates = 'Received: ' + utils.formatDate(order.date)
      if (order.dateClosed) {
        this.orderDates += ', Closed: ' + utils.formatDate(order.dateClosed)
      }
      if (order.deliveryDetails.type === 'inperson') {
        this.deliveryType = 'In Person'
      } else if (order.deliveryDetails.type === 'mail') {
        this.deliveryType = 'Mail'
      } else {
        this.deliveryType = 'undefined'
      }

      const dDetails = order.deliveryDetails
      const paymentDetails = order.paymentDetails

      this.deliveryLocation = null
      if (dDetails && dDetails.address.street) {
        this.deliveryLocation = true
      }

      if (paymentDetails.type === 'inperson') {
        this.paymentType = 'in person'
      } else if (paymentDetails.type === 'creditcard') {
        this.paymentType = 'credit card'
      } else {
        this.paymentType = paymentDetails.type
      }

      this.isCreditCard = paymentDetails.type === 'creditcard'
      this.paymentReceivedMessage = order.paymentReceived ? 'Payment Received' : 'Payment Not Received'
      this.deliveryMessage = order.delivered ? 'Item(s) Delivered' : 'Item(s) Not Delivered'

      if (dDetails.type === 'inperson') {
        this.deliveryInstructions = 'After you deliver the item(s), check the checkbox to complete the order.'
      } else if (dDetails.type === 'mail') {
        this.deliveryInstructions = 'After you mail the item(s), check the checkbox to complete the order.'
      } else {
        this.deliveryInstructions = 'Unknown delivery type: ' + dDetails.type
      }

      if (paymentDetails.type === 'inperson') {
        this.paymentInstructions = 'After you receive the money, check the checkbox to show that you have the payment.'
      } else if (paymentDetails.type === 'creditcard') {
        const stripeChange = order.stripeCharge
        this.paymentInstructions = 'This payment should be automatically collected by Stripe. The Stripe charge ID is: ' + stripeChange.id
        if (order.stripeCharge.refunded) {
          this.creditCardStatus = 'Payment refunded'
          this.showRefundButton = false
          this.paymentClass = 'complete warning'
          this.paymentReceivedMessage = 'Refunded'
        } else if (order.stripeCharge.paid) {
          this.creditCardStatus = 'Paid'
          this.showRefundButton = true
        } else {
          this.creditCardStatus = order.stripeCharge.status
          this.showRefundButton = true
        }
        this.stripeTransactionID = order.stripeCharge.id
      } else {
        this.paymentInstructions = 'Unknown payment type: ' + paymentDetails.type
      }

      this.loading = false
      this.notfound = false

      return
    },

    loadOrder: function (oid, orders) {

      this.loading = true
      this.notfound = false

      if (this.orderWatcher) {
        this.orderWatcher()
        this.orderWatcher = null
      }

      // Just load up all the details we need to show in the form.
      if (oid && orders) {
        for (let i = 0; i < orders.length; i++) {
          if (orders[i].id === oid) {
            // We have an order
            this._showOrder(orders[i])
            return
          }
        }
        console.log("HAve to load order special")

        // We need to load the order from the DB
        let orderRef = firebase.firestore()
          .collection('artists')
          .doc(this.uid)
          .collection('orders')

       this.orderWatcher = orderRef.doc(oid).onSnapshot((docSnapshot) => {
          if (docSnapshot.exists) {
            const docData = docSnapshot.data()
            docData.id = docSnapshot.id

            // Show it!
            this._showOrder(docData)
          } else {
            // We couldn't find it.
            this.notfound = true
            this.loading = false
          }
        })
      }
    },
    sendRefund: async function () {
      // Send a Stripe refund for this order
      this.refundProcessing = false
      this.refundError = null
      this.refundSucceeded = false
      this.$confirm(`Are you sure you want to send a refund for this order?`).then(async res => {
        if (res) {
          this.refundProcessing = true
          var refundFunction = config.getFunctions().httpsCallable('refundOrder')

          // Call the async function to refund
          try {
            if (this.locked) {
              throw Error('Account is locked. You cannot make any changes.')
            }

            await refundFunction({
              'orderID': this.oid
            })
            this.refundSucceeded = true
          } catch (err) {
            console.error('ERROR refunding: ' + JSON.stringify(err))
            this.refundError = err.message
          }
          this.refundProcessing = false
        }
      })
    },
    resendInvoice: async function () {
      this.sendingInvoice = true
      this.resendError = null
      this.resendSuccess = null
      // Get the Order ID
      try {
        if (this.locked) {
          throw Error('Account is locked. You cannot make any changes.')
        }

        // Now link them and charge the account
        var resendInvoiceFunction = config.getFunctions().httpsCallable('resendInvoice')
        const rc = await resendInvoiceFunction(
          {
            orderID: this.oid
          }
        )

        console.log('RC from resendInvoice:' + JSON.stringify(rc))

        // If all went well, we're logged in as the kid, go to their account
        this.resendSuccess = true
      } catch (err) {
        console.error('ERROR resending the invoice: ' + JSON.stringify(err))
        this.resendError = err.message
      }

      this.sendingInvoice = false
    },
    deleteOrder: function (order) {
      console.log('Delete order: should confirm')
      this.$store.dispatch('admin/deleteOrder', {'oid': order.id})
    },
    editOrder: function (order) {
      this.$emit('viewOrder', order)
    },
    togglePaymentReceived: function () {
      this.paymentError = null
      if (this.locked) {
        this.paymentError = 'Account is locked. You cannot make any changes.'
        return
      }

      this.paymentLoading = true
      this.$store.dispatch('admin/setPaymentReceived', {'oid': this.oid, 'received': !this.order.paymentReceived}).then(() => {
        this.paymentLoading = false
      })
    },
    toggleDelivery: function () {
      this.deliveryError = null
      if (this.locked) {
        this.deliveryError = 'Account is locked. You cannot make any changes.'
        return
      }


      this.deliveryLoading = true
      this.$store.dispatch('admin/setDelivery', {'oid': this.oid, 'delivered': !this.order.delivered}).then(() => {
        this.deliveryLoading = false
      })
    },
    viewItem: function (item) {
      // Go to the view item page
      console.log(item)

      this.$router.push({path: '/artists/viewitem/' + item.item.id})
    }
  }
}
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>


tr.bottomLine td {
  border-bottom:1pt solid black;
}

.orderlist {
  width: 100%;
}

.incomplete {
  font-weight: bold;
  color: white;
  padding: 10px;
}

.complete {
  font-weight: bold;
  color: white;
  padding: 10px;
}
</style>
